import { useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import authService from '../services/authService';
import { STORAGE_KEYS } from '../constants';

interface UseAuthReturn {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (phone: string, pin: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const useAuth = (): UseAuthReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        if (currentUser && authService.isAuthenticated()) {
          // Verify token is still valid
          const isValid = await authService.verifyToken();
          if (isValid) {
            // Check if user role is allowed for admin panel
            const allowedRoles = ['super_admin', 'cluster_level_manager', 'store_manager', 'mis'];
            if (allowedRoles.includes(currentUser.role)) {
              setUser(currentUser);
            } else {
              // User role not allowed, clear auth data
              await authService.logout();
            }
          } else {
            // Token is invalid, clear auth data
            await authService.logout();
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        await authService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (phone: string, pin: string) => {
    try {
      setIsLoading(true);
      const authResponse = await authService.login({ phone, pin });
      
      // Allow Admin Panel access for Super Admin, CLM, Store Manager, and MIS
      const userRole = authResponse.user.role;
      const allowedRoles = ['super_admin', 'cluster_level_manager', 'store_manager', 'mis'];
      if (!allowedRoles.includes(userRole)) {
        // Clear any stored data
        await authService.logout();
        throw new Error('You are not authorized to use the Admin Panel. Please use the mobile app to login.');
      }
      
      setUser(authResponse.user);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userProfile = await authService.getProfile();
      setUser(userProfile);
      localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(userProfile));
    } catch (error) {
      console.error('Refresh user error:', error);
      throw error;
    }
  }, []);

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  };
};
